/**
 * "SDKs" overview — SDKs section.
 *
 * Presents the full set of server-side intent-verification SDKs (Node plus the
 * seven non-Node languages) in one place: what a server-side SDK does, when you
 * need one, and the install command for every language. Node links to the
 * quickstart (which already covers `@syncanix/sdk-node`); each non-Node language
 * links to its own page. The combined install block references the D27
 * `PUBLISHED_PACKAGES` SoT, so the "available at launch" note renders itself
 * while the packages are still pre-registry.
 */
import type { DocPageContent } from '../types';

const INSTALL_MATRIX = `# Node.js (Express, NestJS, Fastify, Next.js)
npm install @syncanix/sdk-node
# Python (Django, Flask, FastAPI)
pip install syncanix-sdk
# Go (net/http, Gin, Echo, Fiber)
go get github.com/syncanix-ai/syncanix-sdk-go
# Spring Boot (JVM)
implementation "ai.syncanix:syncanix-sdk-spring-boot"
# PHP (Laravel, Symfony)
composer require syncanix/sdk-php
# Ruby (Rails, Rack)
bundle add syncanix-sdk-rails
# Rust (Axum, Actix Web, Tower)
cargo add syncanix-sdk-rust
# .NET (ASP.NET Core)
dotnet add package Syncanix.Sdk.AspNetCore`;

export const SDKS_OVERVIEW_PAGE: DocPageContent = {
  slug: 'sdks',
  titleKey: 'docs.sdk.overview.title',
  descriptionKey: 'docs.sdk.overview.description',
  blocks: [
    { kind: 'paragraph', textKey: 'docs.sdk.overview.lead' },

    { kind: 'heading', level: 2, id: 'what', textKey: 'docs.sdk.overview.what.heading' },
    { kind: 'paragraph', textKey: 'docs.sdk.overview.what.body' },
    {
      kind: 'list',
      itemKeys: [
        'docs.sdk.overview.what.point.verify',
        'docs.sdk.overview.what.point.actasuser',
        'docs.sdk.overview.what.point.stateless',
      ],
    },

    { kind: 'heading', level: 2, id: 'when', textKey: 'docs.sdk.overview.when.heading' },
    { kind: 'paragraph', textKey: 'docs.sdk.overview.when.body' },
    {
      kind: 'callout',
      tone: 'important',
      titleKey: 'docs.sdk.overview.when.callout.title',
      textKey: 'docs.sdk.overview.when.callout.body',
      links: {
        runtime: {
          target: { docSlug: 'reference/runtime-api' },
          labelKey: 'docs.sdk.overview.when.callout.link',
        },
      },
    },

    { kind: 'heading', level: 2, id: 'install', textKey: 'docs.sdk.overview.install.heading' },
    { kind: 'paragraph', textKey: 'docs.sdk.overview.install.body' },
    { kind: 'code', language: 'bash', code: INSTALL_MATRIX },

    { kind: 'heading', level: 2, id: 'languages', textKey: 'docs.sdk.overview.languages.heading' },
    { kind: 'paragraph', textKey: 'docs.sdk.overview.languages.body' },
    {
      kind: 'cardGrid',
      cards: [
        {
          titleKey: 'docs.sdk.overview.card.node.title',
          textKey: 'docs.sdk.overview.card.node.text',
          to: { docSlug: 'quickstart' },
        },
        {
          titleKey: 'docs.sdk.python.title',
          textKey: 'docs.sdk.overview.card.python.text',
          to: { docSlug: 'sdks/python' },
        },
        {
          titleKey: 'docs.sdk.go.title',
          textKey: 'docs.sdk.overview.card.go.text',
          to: { docSlug: 'sdks/go' },
        },
        {
          titleKey: 'docs.sdk.jvm.title',
          textKey: 'docs.sdk.overview.card.jvm.text',
          to: { docSlug: 'sdks/jvm' },
        },
        {
          titleKey: 'docs.sdk.php.title',
          textKey: 'docs.sdk.overview.card.php.text',
          to: { docSlug: 'sdks/php' },
        },
        {
          titleKey: 'docs.sdk.ruby.title',
          textKey: 'docs.sdk.overview.card.ruby.text',
          to: { docSlug: 'sdks/ruby' },
        },
        {
          titleKey: 'docs.sdk.rust.title',
          textKey: 'docs.sdk.overview.card.rust.text',
          to: { docSlug: 'sdks/rust' },
        },
        {
          titleKey: 'docs.sdk.dotnet.title',
          textKey: 'docs.sdk.overview.card.dotnet.text',
          to: { docSlug: 'sdks/dotnet' },
        },
      ],
    },

    { kind: 'heading', level: 2, id: 'next', textKey: 'docs.common.nextSteps' },
    {
      kind: 'cardGrid',
      cards: [
        {
          titleKey: 'docs.sdk.overview.next.runtime.title',
          textKey: 'docs.sdk.overview.next.runtime.text',
          to: { docSlug: 'reference/runtime-api' },
        },
        {
          titleKey: 'docs.sdk.overview.next.actasuser.title',
          textKey: 'docs.sdk.overview.next.actasuser.text',
          to: { docSlug: 'guides/act-as-user' },
        },
      ],
    },
  ],
};
